import React, { useEffect, useState } from 'react';
import { sprintf } from 'sprintf-js';
import styled from 'styled-components';

import { TileGridBlockData, TileGridTile } from 'api/cms';
import Box from 'components/Box';
import { Button } from 'components/Button';
import Icon from 'components/Icon';
import { FilestackImage } from 'components/Image';
import { Body, BodySmall, H2, H3, H4 } from 'components/Text';
import { breakpoints, colors, gutterWidth, transition } from 'theme';
import { imageUrl } from 'utils/filestack';
import { retinaBackgroundImage } from 'utils/style';

const filestackTransform = (width: number, height: number) =>
  `resize=width:${width},height:${height},fit:clip,align:center/quality=value:70/output=format:jpg`;

const Modal = styled(Box)`
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
`;
const Image = styled(Box)<{ handle: string; mimetype: string }>`
  background-size: cover;
  background-position: center;
  ${({ handle, mimetype }) =>
    retinaBackgroundImage(
      imageUrl({
        handle,
        mimetype,
        transform: filestackTransform(640, 640),
      }),
      {
        filename2x: imageUrl({
          handle,
          mimetype,
          transform: filestackTransform(1280, 1280),
        }),
        include3x: false,
      }
    )};
`;
const CloseButton = styled(Button)`
  transition: color ${transition};
  &:hover,
  &:focus {
    color: ${colors.waterBlue};
  }
`;
const ImageContainer = styled(Box)`
  ${({ $smallImage }) =>
    $smallImage &&
    `
    img {
      display: block;
      width: auto;
      margin: 0 auto;
    }
  `}
`;
const Tile = styled(
  ({
    tile: { heading, subHeading, description, thumbnail, image },
    isOpen,
    open,
    close,
    isPage,
    smallImage,
    blockIndex,
    ...props
  }: {
    tile: TileGridTile;
    isOpen: boolean;
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    open: (...args: Array<any>) => any;
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    close: (...args: Array<any>) => any;
    isPage: boolean;
    smallImage: boolean;
    blockIndex: number;
  }) => {
    const imageWidth = isPage ? 238 : 190;
    return (
      <>
        <Box
          component={Button}
          onClick={open}
          display="flex"
          justifyContent="flex-start"
          alignItems="center"
          flexDirection="column"
          p={['20px 0 !important', null, '32px 24px !important', null]}
          textAlign="center"
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...props}
        >
          <ImageContainer
            width={imageWidth}
            overflow="hidden"
            mx="auto"
            mb={smallImage ? 24 : 36}
            borderRadius={smallImage ? undefined : '50%'}
            $smallImage={smallImage}
          >
            <FilestackImage
              data={thumbnail}
              data-qa-id={`${smallImage ? 'small' : 'large'}-image`}
              alt={sprintf(getText('Image of %(heading)s'), {
                heading,
              })}
              width={imageWidth}
              height={smallImage ? 80 : imageWidth}
              fit={smallImage ? 'max' : 'crop'}
              format={smallImage ? undefined : 'jpg'}
              quality={smallImage ? 100 : undefined}
            />
          </ImageContainer>
          <H4 mb={12} mt={0} color={colors.waterBlue}>
            {heading}
          </H4>
          <BodySmall>{subHeading}</BodySmall>
        </Box>
        {isOpen && (
          <Modal
            position="fixed"
            zIndex="99999"
            top={0}
            left={0}
            width={1}
            height="100%"
            bg="white"
            p={[gutterWidth / 2, null, null, gutterWidth]}
          >
            <Box
              position="relative"
              width={[
                1,
                1,
                `calc(100% + ${gutterWidth / 4}px)`,
                `calc(100% + ${gutterWidth / 2}px)`,
              ]}
              minHeight="100%"
              pt={[gutterWidth, null, 0]}
              mx={[0, null, -gutterWidth / 4, -gutterWidth / 2]}
              display="flex"
              flexDirection={['column', null, 'row']}
              justifyContent={['flex-start', null, 'center']}
              alignItems={['center', null, 'stretch']}
            >
              <Box
                px={[0, null, gutterWidth / 4, gutterWidth / 2]}
                width={[1, null, 1 / 2]}
                maxWidth={[480, 640, 'none']}
                maxHeight={[480, 640, 'none']}
                flex="0 0 auto"
              >
                <Image
                  pb={['100%', null, null, 0]}
                  height="100%"
                  handle={image.handle}
                  mimetype={image.mimetype}
                />
              </Box>
              <Box
                px={[0, null, gutterWidth / 4, gutterWidth / 2]}
                mt={[gutterWidth, null, 0]}
                width={[1, null, 1 / 2]}
                maxWidth={[480, null, 640]}
                flex="0 0 auto"
                display="flex"
                flexDirection="column"
                justifyContent="center"
              >
                <H2 mb={0}>{heading}</H2>
                <H3 my={18} fontWeight="normal">
                  {subHeading}
                </H3>
                <Body
                  analyticsData={{
                    eventName: 'CMS Block',
                    eventProperties: {
                      block_position: blockIndex,
                      block_type: 'Tile Grid',
                    },
                  }}
                  dangerouslySetInnerHTML={{
                    __html: description,
                  }}
                  styleAs={description.length > 360 ? 'bodySmall' : 'body'}
                />
              </Box>
              <Box
                position={['fixed', null, 'absolute']}
                top={0}
                right={0}
                left={[0, null, 'auto']}
                zIndex={1}
                display="flex"
                justifyContent="flex-end"
                bg="white"
                boxShadow={['0 2px 4px rgba(0,0,0,0.1)', null, 'none']}
                px={[gutterWidth / 2, null, 0]}
                py={[gutterWidth / 4, null, 0]}
              >
                <CloseButton
                  data-qa-id="cms-tg-close-button"
                  onClick={close}
                  color={colors.battleshipGrey}
                >
                  <Box display="flex" alignItems="center">
                    <BodySmall tag="span" mr="6px">
                      {getText('close')}
                    </BodySmall>
                    <Icon
                      name="close"
                      display="block"
                      width={36}
                      height={36}
                      stroke="currentColor"
                      strokeWidth={0.5}
                    />
                  </Box>
                </CloseButton>
              </Box>
            </Box>
          </Modal>
        )}
      </>
    );
  }
)`
  transition: all ${transition};
  @media (min-width: ${breakpoints[1]}) {
    &:hover,
    &:focus {
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
      border-radius: 3px;
      h4 {
        text-decoration: underline;
      }
    }
  }
`;

type SectionButtonProps = { $active?: boolean };

const SectionButton = styled(Button).attrs<SectionButtonProps>(
  ({ $active }) => ({
    style: {
      color: $active ? colors.waterBlue : colors.battleshipGrey,
    },
  })
)<SectionButtonProps>`
  position: relative;
  &::before,
  &:after {
    content: '';
    position: absolute;
    bottom: -16px;
    left: 50%;
    transform: translateX(-50%);
    border: 0;
    opacity: 0;
    transition: all ${transition};
  }
  &::before {
    height: 10px;
    width: calc(100% - 48px);
    border-top: 2px solid currentColor;
  }
  &::after {
    border-color: transparent;
    border-style: solid;
    border-top-width: 5px;
    border-top-color: currentColor;
    border-left-width: 5px;
    border-right-width: 5px;
    border-bottom-width: 5px;
  }
  ${(props) =>
    props.$active
      ? `
    &::before, &::after {
      opacity: 1;
    }
  `
      : `
    &:focus, &:hover {
      &::before, &::after {
        opacity: 0.3;
      }
    }
  `}
`;

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
const TileGridBlock = ({
  blockIndex,
  data: { sections, imageSize },
  isPage,
}: {
  blockIndex: number;
  data: TileGridBlockData;
  isPage: boolean;
}) => {
  const [currentTile, setCurrentTile] = useState(null);
  const [currentSection, setCurrentSection] = useState(0);
  useEffect(() => {
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setCurrentTile(null);
      }
    };

    document.addEventListener('keyup', onKeyUp);
    return () => {
      document.removeEventListener('keyup', onKeyUp);
    };
  }, []);
  return (
    <Box data-qa-id="cms-tile-grid">
      {sections.length > 1 && (
        <Box display="flex" flexWrap="wrap" justifyContent="center" mb={48}>
          {sections.map(({ name }, index) => (
            <SectionButton
              data-qa-id={`cms-tg-${name}`}
              key={name}
              px={24}
              my={16}
              borderLeft={index > 0 ? `1px solid ${colors.aluminum}` : '0'}
              $active={index === currentSection}
              onClick={() => setCurrentSection(index)}
            >
              <H2 my={0} styleAs="body">
                {name}
              </H2>
            </SectionButton>
          ))}
        </Box>
      )}
      {sections
        .filter((section, index) => index === currentSection)
        .map(({ name, tiles }) => (
          <Box key={name}>
            <Box display="flex" flexWrap="wrap" justifyContent="center">
              {tiles.map((tile, index) => (
                <Box
                  key={tile.heading}
                  display="flex"
                  justifyContent="center"
                  width={[1, 1 / 2, 1 / 4]}
                  maxWidth={400}
                  flex="0 0 auto"
                >
                  <Tile
                    tile={tile}
                    isOpen={index === currentTile}
                    isPage={isPage}
                    open={() => setCurrentTile(index)}
                    close={() => setCurrentTile(null)}
                    smallImage={imageSize === 'SMALL'}
                    blockIndex={blockIndex}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        ))}
    </Box>
  );
};

export default TileGridBlock;
